import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Popover, Switch, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Loader from "../../../../components/commonComponents/Loader/Index";
import { ICONS } from "../../../../assets/icons";
import Tags from "../../../crm/components/main/contact/Tags";
import Label from "../../../crm/components/commonComponents/label";
import AddCircle from "../../../../assets/customSVG/AddCircle";
import Tick from "../../../../assets/customSVG/Tick";
import Close from "../../../../assets/customSVG/Close";
import { Address } from "../../../crm/components/commonComponents/location/Address";
import { Province } from "../../../crm/components/commonComponents/location/Province";
import { City } from "../../../crm/components/commonComponents/location/City";
import { hexToRgba } from "../../../../helper/colors";
import { formatInternationalPhone } from "../../helper";
import { updateCustomContactLayout } from "../../api/Api";
import { useFeature } from "../../../../hooks/useFeatureGating";
import { FEATURES } from "../../../../constant/featureSlugs";

// ============== StrictModeDroppable Wrapper ==============
// This fixes "Unable to find draggable" error in React 18 Strict Mode
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};
// ==========================================================

const cloneSections = (sections = []) =>
  sections.map((section) => ({
    ...section,
    items: (section.items || []).map((item) => ({ ...item })),
  }));

const getPositionValue = (value, fallback) => {
  if (value === undefined || value === null) return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const sortByPosition = (items = []) =>
  (items || [])
    .map((item, index) => ({
      item,
      order: getPositionValue(item?.position, index + 1),
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);

const isVisibleFlag = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  if (typeof value === "number") {
    return value === 1;
  }
  return value === true;
};

const normalizeSections = (sections = []) =>
  sortByPosition(sections).map((section, sectionIndex) => {
    const items = sortByPosition(section.fields || section.attributes || []).map((item, itemIndex) => ({
      ...item,
      id: item?.id,
      position: getPositionValue(item?.position, itemIndex + 1),
      visible: isVisibleFlag(item?.visible),
      itemId: item?.id ?? `${section.id}-item-${itemIndex}`,
    }));

    return {
      ...section,
      title: section.title || section.label || section.name || "Untitled Section",
      type: section.type,
      position: getPositionValue(section?.position, sectionIndex + 1),
      visible: isVisibleFlag(section?.visible),
      items,
    };
  });

const mapFieldsForPayload = (items = []) =>
  items.map((item, index) => ({
    id: item.id,
    kind: item.kind,
    key: item.key,
    label: item.label,
    visible: isVisibleFlag(item.visible),
    position: getPositionValue(item.position, index + 1),
  }));

const mapAttributesForPayload = (items = []) =>
  items.map((item, index) => ({
    id: item.id,
    status: item.status,
    name: item.name,
    label: item.label,
    attribute_type: item.attribute_type,
    default: item.default,
    visible: isVisibleFlag(item.visible),
    position: getPositionValue(item.position, index + 1),
  }));

const buildLayoutPayload = (sections = []) => ({
  layout: {
    sections: sections.map((section, index) => {
      const base = {
        id: section.id,
        type: section.type,
        title: section.title || section.label || section.name,
        visible: section.visible !== false,
        position: section.position ?? index + 1,
      };

      if (section.type === "overview") {
        return {
          ...base,
          fields: mapFieldsForPayload(section.items),
        };
      }

      if (section.type === "scope") {
        return {
          ...base,
          scope_id: section.scope_id,
          name: section.name,
          status: section.status,
          label: section.label,
          attributes: mapAttributesForPayload(section.items),
        };
      }

      if (section.type === "tags") {
        return base;
      }

      return base;
    }),
  },
});

const CustomContactLayout = ({
  brandId = 1,
  selectedPerson,
  fullName,
  email,
  contactOrigin,
  contactPopover,
  setContactPopover,
  attributes,
  infoOpen,
  setInfoOpen,
  editedScope,
  setEditedScope,
  restrictedAttributes,
  editedAttributes,
  setEditedAttributes,
  tempValues,
  setTempValues,
  editingField,
  setEditingField,
  popoverVisible,
  setPopoverVisible,
  selectedCountry,
  selectedState,
  selectedCity,
  handleCountrySelect,
  handleStateSelect,
  handleCitySelect,
  countryList,
  stateList,
  cityList,
  showErrorToast,
  notification,
  gender,
  handleSaveAttribute,
  isSaving,
  getInputType,
  loadingBusinessNumber,
  businessNumberDetails,
  isTagPopoverOpen,
  setIsTagPopoverOpen,
  handleAssignTag,
  handleRemoveTag,
  closeAction,
  isCustomizerOpen,
  setIsCustomizerOpen,
  showSuccessToast,
  layoutSections = [],
  layoutLoading = false,
  layoutError = "",
  onLayoutRefresh,
}) => {
  const { isEnabled: canUseCustomAttributes } = useFeature(FEATURES.CUSTOM_ATTRIBUTES);
  const [baseSections, setBaseSections] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const EllipsisText = ({ text, className = "", align = "left" }) => {
    const textRef = React.useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
      const element = textRef.current;
      if (!element) return;
      const checkOverflow = () => {
        if (!element) return;
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      };
      checkOverflow();
      window.addEventListener("resize", checkOverflow);
      return () => {
        window.removeEventListener("resize", checkOverflow);
      };
    }, [text]);

    const span = (
      <span
        ref={textRef}
        className={className}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: align,
        }}
      >
        {text}
      </span>
    );

    if (!isOverflowed) {
      return span;
    }

    return <Tooltip title={text}>{span}</Tooltip>;
  };

  const KeyValueRow = ({ label, value, className = "" }) => {
    const normalizedLabel = label || "Field";
    const normalizedValue =
      value === undefined || value === null || value === "" ? "-" : value;
    const labelText =
      typeof normalizedLabel === "string" ? normalizedLabel : String(normalizedLabel);
    const valueText =
      typeof normalizedValue === "string" ? normalizedValue : String(normalizedValue);

    return (
      <div
        className={`w-full py-2 ${className}`}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 45%) minmax(0, 55%)",
          columnGap: "12px",
          alignItems: "center",
        }}
      >
        <EllipsisText text={labelText} className="sidebar-sub-header" />
        <EllipsisText text={valueText} className="new-body-text" align="left" />
      </div>
    );
  };
  
  const normalizedSections = useMemo(
    () => normalizeSections(layoutSections || []),
    [layoutSections],
  );

  useEffect(() => {
    const cloned = cloneSections(normalizedSections);
    setBaseSections(cloned);
    setCustomSections(cloneSections(cloned));
  }, [normalizedSections]);

  const layoutSignature = useMemo(() => JSON.stringify(baseSections), [baseSections]);
  const customSignature = useMemo(() => JSON.stringify(customSections), [customSections]);
  const hasPendingChanges = layoutSignature !== customSignature;

  const visibleSections = useMemo(() => {
    return cloneSections(customSections)
      .filter((section) => section.visible)
      .sort((a, b) => a.position - b.position);
  }, [customSections]);

  const getVisibleItems = useCallback((section) => {
    return (section.items || [])
      .filter((item) => item.visible)
      .sort((a, b) => a.position - b.position);
  }, []);

  const handleSectionToggle = (sectionId, forcedValue = null) => {
    setCustomSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        const nextVisible = forcedValue === null ? !section.visible : forcedValue;
        return {
          ...section,
          visible: nextVisible,
          items:
            section.type === "scope"
              ? section.items.map((item) => ({ ...item, visible: nextVisible }))
              : section.items,
        };
      }),
    );
  };

  const handleItemToggle = ({ sectionId, itemId }) => {
    setCustomSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        const updatedItems = section.items.map((item) =>
          item.itemId === itemId ? { ...item, visible: !item.visible } : item,
        );
        const hasVisibleItem = updatedItems.some((item) => item.visible);
        return {
          ...section,
          items: updatedItems,
          visible: section.type === "scope" ? hasVisibleItem : section.visible,
        };
      }),
    );
  };

  const reorderItems = (sectionId, startIndex, endIndex) => {
    if (startIndex === endIndex) return;
    setCustomSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        const items = Array.from(section.items);
        const [removed] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, removed);
        return {
          ...section,
          items: items.map((item, index) => ({ ...item, position: index + 1 })),
        };
      }),
    );
  };

  const reorderSections = (startIndex, endIndex) => {
    if (startIndex === endIndex) return;
    setCustomSections((prev) => {
      const next = Array.from(prev);
      const [removed] = next.splice(startIndex, 1);
      next.splice(endIndex, 0, removed);
      return next.map((section, index) => ({ ...section, position: index + 1 }));
    });
  };

  const handleDragEnd = (result) => {
    const { destination, source, type } = result;
    if (!destination) return;

    if (type === "section") {
      reorderSections(source.index, destination.index);
      return;
    }

    if (type === "item") {
      if (source.droppableId !== destination.droppableId) return;
      const sectionId = source.droppableId.replace("items-", "");
      reorderItems(sectionId, source.index, destination.index);
    }
  };

  const resetLayout = () => {
    setCustomSections(cloneSections(baseSections));
  };

  const handleSaveLayout = async () => {
    if (!hasPendingChanges || isSavingLayout) return;
    setIsSavingLayout(true);
    try {
      const payload = buildLayoutPayload(customSections);
      await updateCustomContactLayout(brandId || 1, payload);
      const updated = cloneSections(customSections);
      setBaseSections(updated);
      setCustomSections(cloneSections(updated));
      setIsCustomizerOpen(false);
      if (showSuccessToast && notification) {
        showSuccessToast(notification, "Layout updated successfully");
      }
      if (onLayoutRefresh) {
        Promise.resolve(
          onLayoutRefresh({ shouldReloadAttributes: false })
        ).catch(() => {});
      }
    } catch (error) {
      if (showErrorToast && notification) {
        showErrorToast(
          notification,
          error?.response?.data?.error || error?.message || "Unable to update layout."
        );
      }
    } finally {
      setIsSavingLayout(false);
    }
  };

  const getOverviewValue = (fieldKey) => {
    switch (fieldKey) {
      case "name": {
        const displayName = fullName || selectedPerson?.profile_name || "";
        return displayName || "-";
      }
      case "phone":
        return selectedPerson?.to_number
          ? `+${selectedPerson?.phone_country_code || ""} ${selectedPerson?.to_number}`
          : "-";
      case "email":
        return email || "-";
      case "from_number":
        return selectedPerson?.from_number
          ? `+${formatInternationalPhone(selectedPerson?.from_number)}`
          : "-";
      case "contact_origin":
        return contactOrigin || "-";
      default:
        return selectedPerson?.[fieldKey] || "-";
    }
  };

  const getScopeDetails = useCallback(
    (section) => {
      const scope =
        attributes?.scopes?.find(
          (instance) =>
            String(instance?.id) === String(section.scope_id) ||
            instance?.name === section.name ||
            instance?.label === section.label,
        ) || null;

      if (!scope) {
        return {
          scope,
          scopeAttributes: [],
        };
      }

      const visibleItems = getVisibleItems(section);
      const attrOrder = new Map();
      visibleItems.forEach((item, index) => {
        if (item?.id) {
          attrOrder.set(String(item.id), index);
        }
      });

      const scopeAttributes = (scope.profile_attributes || [])
        .filter((attribute) => attrOrder.has(String(attribute.id)))
        .sort(
          (a, b) =>
            (attrOrder.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER) -
            (attrOrder.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER),
        );

      return { scope, scopeAttributes };
    },
    [attributes?.scopes, getVisibleItems],
  );

  const renderScopeEditFields = (scope, scopeAttributes) => (
    <div style={{ width: 400, padding: "16px" }}>
      <div className="flex-column" style={{ gap: "16px" }}>
        {scopeAttributes
          .filter((attr) => !restrictedAttributes.includes(attr.name))
          .map((attr) => (
            <div key={attr.id}>
              <div className="flex-row space-between align-center mb-1">
                <div className="font13px-600">{attr.label || attr.name}</div>
              </div>
              <div className="flex-row space-between align-center">
                {["country", "province", "state", "city"].includes(attr.name) ? (
                  <div className="w-full">
                    <Popover
                      trigger="click"
                      placement="bottomLeft"
                      open={
                        editingField === attr.id &&
                        (popoverVisible.country || popoverVisible.state || popoverVisible.city)
                      }
                      onOpenChange={(open) => {
                        if (open) {
                          setEditingField(attr.id);
                          if (attr.name === "country") {
                            setPopoverVisible({ country: true, state: false, city: false });
                          } else if (["province", "state"].includes(attr.name)) {
                            if (!selectedCountry.id) {
                              showErrorToast(notification, "Please select a country first");
                              return;
                            }
                            setPopoverVisible({ country: false, state: true, city: false });
                          } else if (attr.name === "city") {
                            if (!selectedState.id) {
                              showErrorToast(notification, "Please select a state/province first");
                              return;
                            }
                            setPopoverVisible({ country: false, state: false, city: true });
                          }
                        } else {
                          setPopoverVisible({ country: false, state: false, city: false });
                        }
                      }}
                      content={
                        <div className="w-64">
                          {attr.name === "country" ? (
                            <Address
                              countryListss={countryList}
                              selectedLocation={{
                                country: selectedCountry,
                                state: selectedState,
                                city: selectedCity,
                              }}
                              handleLocationClick={(type, name, id) => {
                                if (type === "country") {
                                  handleCountrySelect({ id, name });
                                }
                              }}
                              locationType="primary"
                            />
                          ) : ["province"].includes(attr.name) ? (
                            <Province
                              stateList={stateList}
                              selectedLocation={{
                                country: selectedCountry,
                                state: selectedState,
                                city: selectedCity,
                              }}
                              handleLocationClick={(type, name, id) => {
                                if (type === "state") {
                                  handleStateSelect({ id, name });
                                }
                              }}
                              locationType="primary"
                            />
                          ) : (
                            <City
                              cityList={cityList}
                              selectedLocation={{
                                country: selectedCountry,
                                state: selectedState,
                                city: selectedCity,
                              }}
                              handleLocationClick={(type, name, id) => {
                                if (type === "city") {
                                  handleCitySelect({ id, name });
                                }
                              }}
                              locationType="primary"
                            />
                          )}
                        </div>
                      }
                    >
                      <div className="flex items-center justify-between p-2 border rounded bg-white">
                        <span>
                          {attr.name === "country"
                            ? selectedCountry.name || "Select Country"
                            : ["province", "state"].includes(attr.name)
                              ? selectedState.name || "Select State/Province"
                              : selectedCity.name || "Select City"}
                        </span>
                        <DownOutlined className="text-xs" />
                      </div>
                    </Popover>
                  </div>
                ) : getInputType(attr.attribute_type, attr.name) === "gender" ? (
                  <Popover
                    content={
                      <div className="flex flex-col max-h-60 overflow-y-auto w-[10vw]">
                        {gender.map((item) => (
                          <div
                            key={item.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                            onClick={() => {
                              setTempValues((prev) => ({
                                ...prev,
                                [attr.id]: item.label,
                              }));
                            }}
                          >
                            <span>{item.label}</span>
                            {tempValues[attr.id] === item.label && (
                              <Tick width="12px" height="12px" strokeWidth="3.2" className="text-green-500" />
                            )}
                          </div>
                        ))}
                      </div>
                    }
                    trigger="click"
                    placement="bottomLeft"
                    overlayClassName="gender-popover"
                  >
                    <Input
                      value={
                        editingField === attr.id ? tempValues[attr.id] || "" : editedAttributes[attr.id] || ""
                      }
                      readOnly
                      placeholder="Select gender"
                      className={editingField === attr.id ? "border-blue-300" : "bg-gray-50"}
                    />
                  </Popover>
                ) : getInputType(attr.attribute_type, attr.name) === "checkbox" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(editingField === attr.id ? tempValues[attr.id] : editedAttributes[attr.id]) === "true"}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setTempValues((prev) => ({
                          ...prev,
                          [attr.id]: checked ? "true" : "false",
                        }));
                        setEditingField(attr.id);
                      }}
                    />
                    <span>{attr.label || attr.name}</span>
                  </div>
                ) : (
                  <Input
                    value={
                      editingField === attr.id ? tempValues[attr.id] || "" : editedAttributes[attr.id] || ""
                    }
                    onChange={(event) => {
                      const { value } = event.target;
                      setTempValues((prev) => ({
                        ...prev,
                        [attr.id]: value,
                      }));
                      setEditingField(attr.id);
                    }}
                    placeholder={`Enter ${attr.label || attr.name}`}
                    readOnly={editingField !== attr.id && getInputType(attr.attribute_type, attr.name) === "gender"}
                    className={editingField === attr.id ? "border-blue-300" : "bg-gray-50"}
                  />
                )}
                {editingField !== attr.id ? (
                  <button
                    className="text-[#7B54FF] text-sm flex items-center gap-1"
                    onClick={() => {
                      setEditingField(attr.id);
                      setTempValues((prev) => ({
                        ...prev,
                        [attr.id]: editedAttributes[attr.id] || "",
                      }));
                    }}
                  >
                    <img src={ICONS?.inboxEdit} alt="edit" style={{ width: "19px", marginLeft: "10px" }} />
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      className="text-gray-500 hover:text-gray-700"
                      onClick={() => {
                        setEditingField(null);
                        setTempValues((prev) => {
                          const next = { ...prev };
                          delete next[attr.id];
                          return next;
                        });
                      }}
                    >
                      <Close stroke="#616874" circle={false} width="1.5vw" height="1.5vw" />
                    </button>
                    <button
                      className={`text-green-500 hover:text-green-700 ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={isSaving}
                      onClick={async () => {
                        let newValue = tempValues[attr.id] || "";
                        if (getInputType(attr.attribute_type, attr.name) === "checkbox") {
                          newValue = tempValues[attr.id] === "true" ? "true" : "false";
                        }
                        await handleSaveAttribute(attr, scope, newValue);
                        setEditedAttributes((prev) => ({
                          ...prev,
                          [attr.id]: newValue,
                        }));
                        setEditingField(null);
                      }}
                    >
                      <Tick width="1vw" height="1vw" strokeWidth="3.2" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  const renderOverviewSection = (section) => {
    const items = getVisibleItems(section);
    if (!items?.length) return null;

    return (
      <div
        key={section.id}
        className="w-[92] b-radius p-[15px] m-10 gap-2 flex-column bg-white"
        style={{ boxShadow: "0px 4px 4px 0px #0000000A" }}
      >
        <Popover
          trigger="click"
          open={contactPopover}
          onOpenChange={(open) => {
            if (!open) {
              setContactPopover(false);
            }
          }}
          placement="center"
          closable={false}
          arrow={false}
          content={<p className="px-2 py-1 font13px-500">Contact Details</p>}
        >
          <div className=" w-100 flex-row space-between">
            <div className="flex-row align-center gap-2">
              <span className="capitalize font-[500] text-[15px]">
                {section.title || section.label || "Overview"}
              </span>
            </div>
          </div>
        </Popover>
        <span className="border-b-[1px]" />
        <div className="w-100">
          {items.map((item) => (
            <KeyValueRow
              key={item.itemId}
              label={item.label || item.key || "Field"}
              value={getOverviewValue(item.key)}
            />
          ))}
          {selectedPerson?.from_number && (
            <div className="w-100 flex-column mt-10 mb-10">
              <KeyValueRow
                label="From Number"
                value={`+ ${formatInternationalPhone(selectedPerson?.from_number)}`}
              />
              {!loadingBusinessNumber && businessNumberDetails && (
                <div className="w-100 flex-column gap-5 mt-20">
                  <KeyValueRow
                    label="Display Name"
                    value={businessNumberDetails.send_from_phone_name || "-"}
                  />
                </div>
              )}
            </div>
          )}
          {contactOrigin && (
            <KeyValueRow label="Contact Origin" value={contactOrigin} />
          )}
        </div>
      </div>
    );
  };

  const renderScopeSection = (section) => {
    const { scope, scopeAttributes } = getScopeDetails(section);
    if (!scope || scopeAttributes.length === 0) return null;

    return (
      <div
        key={section.id}
        className="w-[95%] b-radius p-[15px] m-10 gap-0 flex-column bg-white"
        style={{ boxShadow: "0px 4px 4px 0px #0000000A" }}
      >
        <div className="w-100 flex-row space-between align-center pb-3 border-b">
          <span className="capitalize font-[500] text-[15px]">{section.label || section.title || scope.label || scope.name}</span>
          {canUseCustomAttributes && (
            <Popover
              trigger="click"
              open={infoOpen && editedScope?.id === scope.id}
              onOpenChange={(visible) => {
                if (visible) {
                  setEditedScope(scope);
                  const initialValues = {};
                  scopeAttributes
                    .filter((attr) => !restrictedAttributes.includes(attr.name))
                    .forEach((attr) => {
                      initialValues[attr.id] = attr.value || "";
                    });
                  setEditedAttributes(initialValues);
                  setTempValues({});
                  setEditingField(null);
                } else {
                  setEditedScope(null);
                  setEditedAttributes({});
                }
                setInfoOpen(visible);
              }}
              placement="right"
              content={renderScopeEditFields(scope, scopeAttributes)}
            >
              <span className="inbox-edit gap-1 flex-row align-center pointer">
                <img src={ICONS?.inboxEdit} alt="inboxEdit" style={{ width: "16px" }} />
                <span className="text-[#7B54FF]">Edit</span>
              </span>
            </Popover>
          )}
        </div>
        <div className="w-100">
          {!canUseCustomAttributes ? (
            <div className="py-3 px-1">
              <p className="text-[12px] text-gray-500 italic">
                Upgrade to view and manage custom profile attributes.
              </p>
            </div>
          ) : (
            scopeAttributes.map((attribute) => (
              <KeyValueRow
                key={attribute.id}
                label={attribute.label || attribute.name}
                value={attribute.value || "-"}
              />
            ))
          )}
        </div>
      </div>
    );
  };

  const renderTagSection = (section) => (
    <div
      key={section.id}
      className="w-[92] b-radius p-[15px] m-10 gap-2 flex-column bg-white"
      style={{ boxShadow: "0px 4px 4px 0px #0000000A" }}
    >
      <div className="flex-row align-center gap-5">
        <p className="font12px-500">{section.title || section.label || "Tags"}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {attributes?.contact_person_tags_list?.length > 0 ? (
          attributes?.contact_person_tags_list?.map((tag) => (
            <Label
              id={tag?.id}
              key={tag?.id}
              label={tag?.tag}
              color={tag?.display_color}
              style={{
                backgroundColor: hexToRgba(tag?.display_color),
              }}
              mainClassNames="w-40"
              closeAction={() => closeAction(tag?.id)}
              disableHover={true}
            />
          ))
        ) : (
          <span></span>
        )}
      </div>
      <div className="flex-row align-center gap-10">
        <Tags
          toggle={isTagPopoverOpen}
          setVisible={setIsTagPopoverOpen}
          tags={attributes?.contact_person_tags_list || []}
          addFunction={handleAssignTag}
          removeFunction={handleRemoveTag}
          Button={
            <p className="flex text-[#616874] text-[.8vw] items-center font-[500] gap-[.4vw] cursor-pointer mt-10 w-21">
              <AddCircle width="1vw" height="1vw" stroke="#7B54FF" strokeWidth="2.5" />
              <span className="font12px-500">Add a Tag</span>
            </p>
          }
        />
      </div>
    </div>
  );

  const renderSection = (section) => {
    if (section.type === "overview") return renderOverviewSection(section);
    if (section.type === "scope") return renderScopeSection(section);
    if (section.type === "tags") return renderTagSection(section);
    return null;
  };

  const renderCustomizerOverlay = () => {
    if (!isCustomizerOpen) return null;

    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-[#11182740] backdrop-blur-sm p-4 z-[999] transition-opacity duration-200"
      >
        <div
          className="w-full max-w-[460px] max-h-[85vh] bg-white rounded-2xl shadow-2xl px-6 pt-6 py-3 space-y-4 transition-all duration-200"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-[1rem] font-semibold text-[#111827]">Customize Layout</h4>
              <p className="text-[0.75rem] text-[#6B7280] mt-1">
                Drag to reorder, toggle visibility, and save your preferred view.
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIsCustomizerOpen(false)}
                className="rounded-full hover:bg-[#F3F4F6]"
                aria-label="Close customizer"
              >
                <Close stroke="#6B7280" circle={false} width="24px" height="24px" />
              </button>
            </div>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable droppableId="sections" type="section">
              {(provided) => (
                <div
                  className="flex flex-col gap-3 max-h-[58vh] overflow-y-auto pr-1"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {customSections.map((section, sectionIndex) => {
                    // Ensure section.id exists and is properly formatted
                    const sectionId = section.id ? String(section.id) : `section-${sectionIndex}`;
                    
                    return (
                      <Draggable
                        key={sectionId}
                        draggableId={sectionId}
                        index={sectionIndex}
                      >
                        {(sectionProvided, sectionSnapshot) => (
                          <div
                            className="rounded-lg border border-[#E5E7EB] p-4 bg-white"
                            ref={sectionProvided.innerRef}
                            {...sectionProvided.draggableProps}
                            style={{
                              ...sectionProvided.draggableProps.style,
                              // Fix positioning offset when dragging inside modal
                              ...(sectionSnapshot.isDragging),
                              boxShadow: sectionSnapshot.isDragging
                                ? "0 10px 20px rgba(0,0,0,0.12)"
                                : "0px 1px 2px rgba(0,0,0,0.05)",
                              borderColor: sectionSnapshot.isDragging ? "#C7D2FE" : "#E5E7EB",
                            }}
                          >
                            {/* Rest of the content remains the same */}
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="cursor-grab active:cursor-grabbing text-[#9CA3AF] select-none"
                                  {...sectionProvided.dragHandleProps}
                                >
                                  ⋮⋮
                                </span>
                                <p className="text-[0.85rem] font-medium text-[#111827]">
                                  {section.title || "Section"}
                                </p>
                              </div>
                              <Switch
                                size="small"
                                checked={section.visible}
                                onChange={(checked) => handleSectionToggle(section.id, checked)}
                              />
                            </div>
                            {section.items?.length > 0 && section.type === "scope" && (
                              <div className="mt-3 flex flex-col gap-2 pl-6">
                                {section.items.map((item) => (
                                  <div
                                    key={item.itemId}
                                    className="flex items-center justify-between text-[0.8rem] text-[#4B5563]"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{item.label || item.name || "Field"}</span>
                                    </div>
                                    <Switch
                                      size="small"
                                      checked={item.visible}
                                      onChange={() =>
                                        handleItemToggle({
                                          sectionId: section.id,
                                          itemId: item.itemId,
                                        })
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>

                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
          <div className="flex items-center justify-end gap-2 pt-[12px] border-t border-[#F1F1F3]">
            <button
              type="button"
              onClick={handleSaveLayout}
              disabled={!hasPendingChanges || isSavingLayout}
              className={`px-4 py-2 rounded-lg text-[0.85rem] font-semibold text-white transition-colors ${
                !hasPendingChanges || isSavingLayout
                  ? "bg-[#C4B5FD] cursor-not-allowed"
                  : "bg-[#7B54FF] hover:bg-[#6A43F4]"
              }`}
            >
              {isSavingLayout ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderCustomizerOverlay()}
      <div className="overflow-y-scroll bg-[#F9F9FC] h-[90vh]">
        {layoutLoading && (
          <div className="flex items-center justify-center mt-10 h-full w-full flex flex-col items-center justify-center">
            <Loader Width={40} Height={40} />
          </div>
        )}

        {!layoutLoading && layoutError && (
          <div className="flex flex-col items-center text-center gap-3 mt-10 px-4">
            <p className="text-[0.85rem] text-[#EF4444]">{layoutError}</p>
            <button
              type="button"
              onClick={() => {
                if (onLayoutRefresh) {
                  Promise.resolve(
                    onLayoutRefresh({ shouldReloadAttributes: true })
                  ).catch(() => {});
                }
              }}
              className="px-4 py-1 rounded-lg bg-[#7B54FF] text-white text-[0.8rem]"
            >
              Retry
            </button>
          </div>
        )}

        {!layoutLoading && !layoutError && visibleSections.length === 0 && (
          <div className="flex flex-col items-center justify-center mt-10 text-center px-4">
            <p className="text-[0.85rem] text-[#6B7280]">
              No sections are visible. Use the customize button to enable them.
            </p>
          </div>
        )}

        {!layoutLoading && !layoutError && visibleSections.map((section) => renderSection(section))}
      </div>
    </>
  );
};

export default CustomContactLayout;
